import * as React from 'react'
import ReactResponsive from 'react-responsive'

import { createIsType } from '../utils/types'
import { MediaQuery } from './MediaQuery'

type Props = (
  | (MediaSizeProps & NoMediaTypeProps)
  | (MediaTypeProps & NoMediaSizeProps)
  | (MediaSizeProps & MediaTypeProps)) &
  BaseProps

type MediaSizeProps =
  | OnlyMobileProps
  | OnlyMobileAndTabletProps
  | OnlyTabletAndDesktopProps
  | TabletProps
  | UpToDesktopProps
  | DesktopProps
  | UpToLargeDesktopProps
  | LargeDesktopProps

type MediaTypeProps = OnlyScreenProps

interface NoMediaSizeProps {
  tablet?: false
  desktop?: false
  largeDesktop?: false
  upToLargeDesktop?: false
  onlyMobile?: false
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop?: false
}

interface NoMediaTypeProps {
  onlyScreen?: false
}

interface OnlyMobileProps {
  tablet?: false
  desktop?: false
  largeDesktop?: false
  upToLargeDesktop?: false
  onlyMobile: true
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop?: false
}

interface OnlyMobileAndTabletProps {
  tablet?: false
  upToDesktop?: false
  desktop?: false
  largeDesktop?: false
  upToLargeDesktop?: false
  onlyMobile?: false
  onlyMobileAndTablet: true
  onlyTabletAndDesktop?: false
}

interface OnlyTabletAndDesktopProps {
  tablet?: false
  upToDesktop?: false
  desktop?: false
  largeDesktop?: false
  upToLargeDesktop?: false
  onlyMobile?: false
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop: true
}

interface TabletProps {
  tablet: true
  upToDesktop?: false
  desktop?: false
  largeDesktop?: false
  upToLargeDesktop?: false
  onlyMobile?: false
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop?: false
}

interface UpToLargeDesktopProps {
  tablet?: false
  upToDesktop?: false
  desktop?: false
  largeDesktop?: false
  upToLargeDesktop: true
  onlyMobile?: false
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop?: false
}

interface UpToDesktopProps {
  tablet?: false
  upToDesktop: true
  desktop?: false
  largeDesktop?: false
  upToLargeDesktop?: false
  onlyMobile?: false
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop?: false
}

interface DesktopProps {
  tablet?: false
  upToDesktop?: false
  desktop: true
  largeDesktop?: false
  upToLargeDesktop?: false
  onlyMobile?: false
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop?: false
}

interface LargeDesktopProps {
  tablet?: false
  upToDesktop?: false
  desktop?: false
  largeDesktop: true
  upToLargeDesktop?: false
  onlyMobile?: false
  onlyMobileAndTablet?: false
  onlyTabletAndDesktop?: false
}

interface OnlyScreenProps {
  onlyScreen: true
}

interface BaseProps {
  children: React.ReactNode | ((matches: boolean) => React.ReactNode)
}

export const MatchMedia: React.SFC<Props> = props => (
  <ReactResponsive query={getQuery(props)}>{props.children}</ReactResponsive>
)

const getQuery = (mediaProps: Props) => {
  const mediaSizeQuery = getMediaSizeQuery(mediaProps as MediaSizeProps)
  const mediaTypeQuery = getMediaTypeQuery(mediaProps as MediaTypeProps)

  if (!mediaSizeQuery && !mediaTypeQuery) {
    throw new Error('media query no soportada')
  }

  const query = [mediaTypeQuery, mediaSizeQuery].filter(Boolean).join(' and ')
  return query
}

const getMediaTypeQuery = (mediaProps: MediaTypeProps) => {
  if (isOnlyScreenProps(mediaProps)) {
    return MediaQuery.onlyScreen.value
  }
  return null
}

const getMediaSizeQuery = (mediaProps: MediaSizeProps) => {
  if (isOnlyMobileProps(mediaProps)) {
    return MediaQuery.onlyMobile.value
  }
  if (isOnlyMobileAndTabletProps(mediaProps)) {
    return MediaQuery.onlyMobileAndTablet.value
  }
  if (isOnlyTabletAndDesktop(mediaProps)) {
    return MediaQuery.onlyTabletAndDesktop.value
  }
  if (isTabletProps(mediaProps)) {
    return MediaQuery.tablet.value
  }
  if (isUpToDesktopProps(mediaProps)) {
    return MediaQuery.upToDesktop.value
  }

  if (isDesktopProps(mediaProps)) {
    return MediaQuery.desktop.value
  }
  if (isUpToLargeDesktopProps(mediaProps)) {
    return MediaQuery.upToLargeDesktop.value
  }
  if (isLargeDesktopProps(mediaProps)) {
    return MediaQuery.largeDesktop.value
  }
  return null
}

const isOnlyScreenProps = createIsType<MediaTypeProps, OnlyScreenProps>(
  props => props.onlyScreen,
)

const isOnlyMobileProps = createIsType<MediaSizeProps, OnlyMobileProps>(
  props => props.onlyMobile,
)

const isOnlyMobileAndTabletProps = createIsType<
  MediaSizeProps,
  OnlyMobileAndTabletProps
>(props => props.onlyMobileAndTablet)

const isOnlyTabletAndDesktop = createIsType<
  MediaSizeProps,
  OnlyTabletAndDesktopProps
>(props => props.onlyTabletAndDesktop)

const isTabletProps = createIsType<MediaSizeProps, TabletProps>(
  props => props.tablet,
)

const isUpToDesktopProps = createIsType<MediaSizeProps, UpToDesktopProps>(
  props => props.upToDesktop,
)

const isDesktopProps = createIsType<MediaSizeProps, DesktopProps>(
  props => props.desktop,
)

const isUpToLargeDesktopProps = createIsType<
  MediaSizeProps,
  UpToLargeDesktopProps
>(props => props.upToLargeDesktop)

const isLargeDesktopProps = createIsType<MediaSizeProps, LargeDesktopProps>(
  props => props.largeDesktop,
)
